import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { renderLabelLink, calculateNotificationStyle } from '../util/renderHelpers';

import './TextareaField.less';

/**
 * Important note: the onChange prop does not behave like one would expect.
 * It overwrites the existing onChange function entirely. This behavior is left in
 * for backwards compatibility with existing usages of the component.
 * If you want to do something *in addition* to the component acting normally on change,
 * use the `inputOnChange` prop.
 */

const TextareaField = (props) => {
  const {
    // Props here also get passed to the input element
    id,
    required,
    defaultValue,
    value: externalValue = '', // like react-final-form
  } = props;

  const {
    // Props here do not get passed to the input
    label,
    maxChars,
    minChars = 0,
    labelLink,
    textareaStyle,
    textareaClassName,
    textareaProps,
    className,
    onBlur,
    hint,
    inputOnChange,
    ...rest
  } = props;
  const initialInputValue = externalValue || defaultValue || '';
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [focus, setFocus] = useState(false);
  const [hasText, setHasText] = useState(!!initialInputValue);

  const handleChange = (e) => {
    setInputValue(e.target.value);
    setHasText(e.target.value);
    const { onChange } = props;
    if (inputOnChange && typeof inputOnChange === 'function') {
      inputOnChange(e);
    }
    if (onChange && typeof onChange === 'function') {
      onChange(e);
    }
  };

  const maxInvalid =
    maxChars && calculateNotificationStyle('max_invalid', maxChars, inputValue.length);
  const maxWarning =
    maxChars && calculateNotificationStyle('warning', maxChars, inputValue.length, 20);
  const minInvalid =
    minChars && calculateNotificationStyle('min_invalid', false, inputValue.length, 0, minChars);

  const textareaWrapperClassNames = classNames(
    'v2-textarea-field',
    'v2-render-field',
    'zola-ui',
    className,
    {
      'has-error': maxInvalid || (!focus && hasText && minInvalid),
    },
    {
      'has-warning': maxWarning,
    }
  );
  const minCharVisibility = inputValue.length < minChars ? 'visible' : 'hidden';
  const maxCharVisibility = inputValue.length >= minChars ? 'visible' : 'hidden';
  const minCharClassNames = classNames('pull-right', 'char-count__min', minCharVisibility);
  const maxCharClassNames = classNames(
    'pull-right',
    'char-count__max',
    maxCharVisibility,
    { 'text-danger': maxInvalid },
    {
      'text-warning': maxWarning,
    }
  );

  return (
    <div className={textareaWrapperClassNames}>
      {label && id && (
        <label htmlFor={id} className="">
          {label}
          {required && <span className="text-danger danger-asterisk">*</span>}
        </label>
      )}
      {renderLabelLink('desktop', labelLink)}
      <textarea
        {...rest}
        {...textareaProps}
        onFocus={() => setFocus(true)}
        onBlur={() => {
          setFocus(false);
          onBlur();
        }}
        className={classNames('form-control', textareaClassName)}
        onChange={handleChange}
        style={{ verticalAlign: 'top', ...textareaStyle }}
      />
      {hint && <span className="char-count pull-left">{hint}</span>}
      {maxChars && (
        <span className="char-count">
          <span className={maxCharClassNames}>{`${inputValue.length} / ${maxChars}`}</span>
        </span>
      )}
      {minChars > 0 && (
        <span className="char-count">
          <span
            className={classNames(minCharClassNames, {
              'has-error': !focus && hasText && minInvalid,
            })}
          >
            Minimum {minChars} characters
          </span>
        </span>
      )}
      {renderLabelLink('mobile', labelLink)}
    </div>
  );
};

TextareaField.propTypes = {
  id: PropTypes.string,
  defaultValue: PropTypes.string,
  /** Required for accessibility with label */
  label: PropTypes.string,
  /** An id is also required for the label to appear. */
  required: PropTypes.bool,
  /** Maximum number of characters.  Displayed as a character counter below.  Client is responsible for additional validation. */
  maxChars: PropTypes.number,
  /** Minimum number of characters.  Displayed as a character counter below.  Client is responsible for additional validation. */
  minChars: PropTypes.number,
  className: PropTypes.string,
  /** Replaces the normal onChange functionality of RFF. */
  onChange: PropTypes.func,
  /** Fires along with the normal onChange functionality of RFF */
  inputOnChange: PropTypes.func,
  /** Text that appears in the bottom-left of the component, opposite of validation */
  hint: PropTypes.string,
  labelLink: PropTypes.shape({
    url: PropTypes.string,
    copy: PropTypes.string,
  }),
  value: PropTypes.string,
  /** Adds styles directly to the textarea */
  textareaStyle: PropTypes.shape({}),
  /** DEPRECATED: Adds classes directly to the textarea */
  textareaClassName: PropTypes.string,
  /** Other props applied directly to textarea, likes rows or cols */
  textareaProps: PropTypes.shape({}),
  onBlur: PropTypes.func,
};

export default TextareaField;
